import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { User, X, Pencil } from 'lucide-react';
import './MenuModal.css';
import iconPeople from '../assets/images/icons/iconPeople.svg';
import useAnalytics from '../hooks/useAnalytics';

interface MenuModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Array<[string, string]>) => void;
  detectedPII?: Array<[string, string]>;
}

export const MenuModal: React.FC<MenuModalProps> = ({
  isOpen,
  onClose,
  onSave,
  detectedPII = []
}) => {
  const { trackPIIFormUserSelector, trackPIIFormConfirm, trackPIIFormClose } = useAnalytics();
  const [localPII, setLocalPII] = useState<Array<[string, string]>>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isNominee, setIsNominee] = useState(false);
  const [activeTab, setActiveTab] = useState<'participant' | 'nominee'>('participant');
  const [touchedFields, setTouchedFields] = useState<string[]>([]);
  const [originalValues, setOriginalValues] = useState<Record<string, string>>({});
  
  // Initialize local state with detected PII when modal opens
  useEffect(() => {
    if (isOpen) {
      // Create a new array with existing detectedPII
      const initialPII = [...detectedPII];

      // Check if nominee status is present in the PII
      const nomineeItem = initialPII.find(item => item[0] === 'nominee');
      const isNomineeValue = nomineeItem ? 
        (nomineeItem[1].toLowerCase() === 'true') : false;
      
      setIsNominee(isNomineeValue);
      setActiveTab(isNomineeValue ? 'nominee' : 'participant');

      // Make sure all critical fields exist
      const criticalFields = ['participant_name', 'participant_ndis_number', 'address'];
      
      // Add nominee fields if needed
      if (isNomineeValue) {
        criticalFields.push('nominee_name', 'nominee_ndis_number');
      }
      
      // Check if each critical field exists in initialPII
      criticalFields.forEach(field => {
        const exists = initialPII.some(item => item[0] === field);
        if (!exists) {
          // Add missing critical field with empty value
          initialPII.push([field, '']);
        }
      });
      
      setLocalPII(initialPII);
      
      // Save original values for tracking changes
      const origValues: Record<string, string> = {};
      initialPII.forEach(([field, value]) => {
        origValues[field] = value;
      });
      setOriginalValues(origValues);
      
      // Mark all fields as touched so empty fields show validation immediately
      setTouchedFields(criticalFields);
      
      // Validate fields immediately to show empty fields in red
      validateFields(initialPII, isNomineeValue);
    }
  }, [isOpen, detectedPII]);

  const validateForm = (): boolean => {
    // Check if all critical fields have values
    const criticalFields = ['participant_name', 'participant_ndis_number', 'address'];
    
    // Add nominee fields if isNominee is true
    if (isNominee) {
      criticalFields.push('nominee_name', 'nominee_ndis_number');
    }
    
    // First check: all required fields must have values
    for (const field of criticalFields) {
      const value = getFieldValue(field);
      if (!value || value.trim() === '') {
        return false;
      }
    }
    
    // Second check: there must be no validation errors
    if (Object.keys(errors).length > 0) {
      return false;
    }
    
    return true;
  };
  
  // Validate fields and set errors
  const validateFields = (piiData: Array<[string, string]> = localPII, isNomineeState: boolean = isNominee): void => {
    const newErrors: Record<string, string> = {};
    
    // Basic required fields
    const criticalFields = ['participant_name', 'participant_ndis_number', 'address'];
    
    // Add nominee fields if isNominee is true
    if (isNomineeState) {
      criticalFields.push('nominee_name', 'nominee_ndis_number');
    }
    
    criticalFields.forEach(field => {
      const fieldItem = piiData.find(item => item[0] === field);
      const value = fieldItem ? fieldItem[1] : '';
      
      if (!value || value.trim() === '') {
        newErrors[field] = `${getFieldLabel(field)} is required`;
      } else if ((field === 'participant_ndis_number' || field === 'nominee_ndis_number') && !/^\d{9}$/.test(value.trim())) {
        newErrors[field] = `${getFieldLabel(field)} must be exactly 9 digits`;
      }
    });
    
    setErrors(newErrors);
  };

  // Get value for a specific field
  const getFieldValue = (fieldName: string): string => {
    const field = localPII.find(item => item[0] === fieldName);
    return field ? field[1] : '';
  };

  // Handle tab change between participant and nominee
  const handleTabChange = (tab: 'participant' | 'nominee') => {
    // Track the user selector click
    trackPIIFormUserSelector(tab);
    
    setActiveTab(tab);
    const newIsNominee = tab === 'nominee';
    setIsNominee(newIsNominee);
    
    // Update the nominee value in PII
    const updatedPII = [...localPII];
    const nomineeIndex = updatedPII.findIndex(item => item[0] === 'nominee');
    
    if (nomineeIndex >= 0) {
      // Update existing nominee value
      updatedPII[nomineeIndex][1] = newIsNominee.toString();
    } else {
      // Add nominee field if it doesn't exist
      updatedPII.push(['nominee', newIsNominee.toString()]);
    }

    // Add nominee fields if they don't exist and nominee is true
    if (newIsNominee) {
      const nomineeFields = ['nominee_name', 'nominee_ndis_number'];
      nomineeFields.forEach(field => {
        const exists = updatedPII.some(item => item[0] === field);
        if (!exists) {
          updatedPII.push([field, '']);
        }
        
        // Mark nominee fields as touched
        if (!touchedFields.includes(field)) {
          setTouchedFields(prev => [...prev, field]);
        }
      });
    }
    
    setLocalPII(updatedPII);
    
    // Validate fields for the new tab
    validateFields(updatedPII, newIsNominee);
  };

  // Handle input changes
  const handleInputChange = (fieldName: string, value: string) => {
    const updatedPII = [...localPII];
    const fieldIndex = updatedPII.findIndex(item => item[0] === fieldName);
    
    if (fieldIndex >= 0) {
      updatedPII[fieldIndex][1] = value;
    } else {
      updatedPII.push([fieldName, value]);
    }
    
    setLocalPII(updatedPII);
    
    // Mark field as touched
    if (!touchedFields.includes(fieldName)) {
      setTouchedFields(prev => [...prev, fieldName]);
    }
    
    // Validate the field
    if (value.trim() === '') {
      // Field is required but empty
      setErrors(prev => ({
        ...prev,
        [fieldName]: `${getFieldLabel(fieldName)} is required`
      }));
    } else if ((fieldName === 'participant_ndis_number' || fieldName === 'nominee_ndis_number') && !/^\d{9}$/.test(value.trim())) {
      // NDIS number validation
      setErrors(prev => ({
        ...prev,
        [fieldName]: `${getFieldLabel(fieldName)} must be exactly 9 digits`
      }));
    } else {
      // Clear error for this field
      if (errors[fieldName]) {
        const newErrors = {...errors};
        delete newErrors[fieldName];
        setErrors(newErrors);
      }
    }
  };

  // Submit updated PII
  const handleSave = () => {
    // Mark all fields as touched
    const allFields = ['participant_name', 'participant_ndis_number', 'address'];
    if (isNominee) {
      allFields.push('nominee_name', 'nominee_ndis_number');
    }
    setTouchedFields(allFields);
    
    // Validate all fields before submitting
    validateFields();
    
    // Only proceed if all required fields are filled
    if (!validateForm()) {
      return;
    }
    
    // Track changes for each field based on tagging plan
    // Participant fields
    trackPIIFormConfirm('p name', getFieldValue('participant_name') !== originalValues['participant_name']);
    trackPIIFormConfirm('p ndis number', getFieldValue('participant_ndis_number') !== originalValues['participant_ndis_number']);
    trackPIIFormConfirm('p address', getFieldValue('address') !== originalValues['address']);
    
    // Nominee fields if applicable
    if (isNominee) {
      trackPIIFormConfirm('n name', getFieldValue('nominee_name') !== originalValues['nominee_name']);
      trackPIIFormConfirm('n ndis number', getFieldValue('nominee_ndis_number') !== originalValues['nominee_ndis_number']);
    }
    
    // Only include fields that have a value
    const filteredPII = localPII.filter(item => {
      // Always include nominee field
      if (item[0] === 'nominee') return true;
      
      // Keep all detected PII
      const wasDetected = detectedPII.some(pii => pii[0] === item[0]);
      // Keep fields with values
      const hasValue = item[1] && item[1].trim() !== '';
      
      return wasDetected || hasValue;
    });
    
    onSave(filteredPII);
    onClose();
  };

  // Handle close with tracking
  const handleClose = () => {
    // Track close click
    trackPIIFormClose();
    onClose();
  };

  // Get field label for display
  const getFieldLabel = (field: string): string => {
    const labelMap: Record<string, string> = {
      'participant_name': 'Participant name',
      'participant_ndis_number': 'NDIS number',
      'address': 'Address',
      'nominee_name': 'Nominee name',
      'nominee_ndis_number': 'Nominee NDIS number'
    };
    return labelMap[field] || field;
  };

  // Check if a field has an error
  const hasError = (fieldName: string): boolean => {
    return touchedFields.includes(fieldName) && (!!errors[fieldName] || getFieldValue(fieldName).trim() === '');
  };

  const modalContent = (
    <div className="menu-modal">
      <div className="menu-modal__container">
        <div className="menu-modal__intro">
          <p>Please check and complete your plan details below. If you have a nominee, you will need to confirm their details too.</p>
        </div>
        
        {/* Toggle tabs */}
        <div className="menu-modal__tabs">
          <button 
            className={`menu-modal__tab ${activeTab === 'participant' ? 'menu-modal__tab--active' : ''}`}
            onClick={() => handleTabChange('participant')}
            aria-label="Switch to participant details tab"
            aria-selected={activeTab === 'participant'}
            aria-controls="participant-panel"
          >
            Participant details
          </button>
          <button 
            className={`menu-modal__tab ${activeTab === 'nominee' ? 'menu-modal__tab--active' : ''}`}
            onClick={() => handleTabChange('nominee')}
            aria-label="Switch to nominee details tab"
            aria-selected={activeTab === 'nominee'}
            aria-controls="nominee-panel"
          >
            Nominee details
          </button>
        </div>
        
        {/* Form content */}
        <div className="menu-modal__content">
          {activeTab === 'participant' ? (
            <>
              {/* Participant name */}
              <div className="menu-modal__field">
                <label className={`menu-modal__label ${hasError('participant_name') ? 'menu-modal__label--error' : ''}`}>
                  {getFieldLabel('participant_name')}
                </label>
                <input
                  type="text"
                  className={`menu-modal__input ${hasError('participant_name') ? 'menu-modal__input--error' : ''}`}
                  value={getFieldValue('participant_name')}
                  onChange={(e) => handleInputChange('participant_name', e.target.value)}
                  placeholder="Enter participant name"
                />
                {hasError('participant_name') && (
                  <div className="menu-modal__error-message">
                    {errors['participant_name'] || `${getFieldLabel('participant_name')} is required`}
                  </div>
                )}
              </div>
              
              {/* NDIS number */}
              <div className="menu-modal__field">
                <label className={`menu-modal__label ${hasError('participant_ndis_number') ? 'menu-modal__label--error' : ''}`}>
                  {getFieldLabel('participant_ndis_number')}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={9}
                  className={`menu-modal__input ${hasError('participant_ndis_number') ? 'menu-modal__input--error' : ''}`}
                  value={getFieldValue('participant_ndis_number')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    handleInputChange('participant_ndis_number', value);
                  }}
                  placeholder="Enter NDIS number (9 digits)"
                />
                {hasError('participant_ndis_number') && (
                  <div className="menu-modal__error-message">
                    {errors['participant_ndis_number'] || `${getFieldLabel('participant_ndis_number')} is required`}
                  </div>
                )}
              </div>
              
              {/* Address */}
              <div className="menu-modal__field">
                <label className={`menu-modal__label ${hasError('address') ? 'menu-modal__label--error' : ''}`}>
                  {getFieldLabel('address')}
                </label>
                <input
                  type="text"
                  className={`menu-modal__input ${hasError('address') ? 'menu-modal__input--error' : ''}`}
                  value={getFieldValue('address')}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Enter address"
                />
                <div className="menu-modal__helper-text">
                  Residential address including state and postcode
                </div>
                {hasError('address') && (
                  <div className="menu-modal__error-message">
                    {errors['address'] || `${getFieldLabel('address')} is required`}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Nominee name */}
              <div className="menu-modal__field">
                <label className={`menu-modal__label ${hasError('nominee_name') ? 'menu-modal__label--error' : ''}`}>
                  {getFieldLabel('nominee_name')}
                </label>
                <input
                  type="text"
                  className={`menu-modal__input ${hasError('nominee_name') ? 'menu-modal__input--error' : ''}`}
                  value={getFieldValue('nominee_name')}
                  onChange={(e) => handleInputChange('nominee_name', e.target.value)}
                  placeholder="Enter nominee name"
                />
                {hasError('nominee_name') && (
                  <div className="menu-modal__error-message">
                    {errors['nominee_name'] || `${getFieldLabel('nominee_name')} is required`}
                  </div>
                )}
              </div>
              
              {/* Nominee NDIS number */}
              <div className="menu-modal__field">
                <label className={`menu-modal__label ${hasError('nominee_ndis_number') ? 'menu-modal__label--error' : ''}`}>
                  {getFieldLabel('nominee_ndis_number')}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={9}
                  className={`menu-modal__input ${hasError('nominee_ndis_number') ? 'menu-modal__input--error' : ''}`}
                  value={getFieldValue('nominee_ndis_number')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    handleInputChange('nominee_ndis_number', value);
                  }}
                  placeholder="Enter NDIS number (9 digits)"
                />
                {hasError('nominee_ndis_number') && (
                  <div className="menu-modal__error-message">
                    {errors['nominee_ndis_number'] || `${getFieldLabel('nominee_ndis_number')} is required`}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isOpen && (
        <Modal
          title="Confirm your details"
          content={modalContent}
          close={handleClose}
          buttonText="Confirm"
          onButtonClick={handleSave}
          noLines={true}
          icon={<img src={iconPeople} alt="People icon" className="me-2" width={24} height={24} />}
        />
      )}
    </>
  );
}; 